// Checkboxes (not radios) so a selected card can be cleared by clicking it again.

const UPGRADE_INPUT = '[data-addon-selection]';

// Breathing room between the sticky header and a card scrolled under it.
const UPGRADE_SCROLL_GAP = 10;
// .pdp-upgrade__panel transitions over .28s; wait it out before measuring.
const UPGRADE_PANEL_TRANSITION_MS = 290;

function formatUpgradeMoney(cents) {
  return Shopify.formatMoney(cents, window.globalVariables.money_format);
}

// Base price comes from data-actualprice on [data-currentPrice], kept in sync by component-product-form.js.
/** @param {boolean} [announce] Pass true only from the upgrade toggle; variant changes are announced elsewhere. */
function recalcUpgradeTotal(announce) {
  const totalEl = document.querySelector('[data-mainProPrice]');
  if (!totalEl) return;

  const priceEl = document.querySelector('[data-currentPrice]');
  let cents = priceEl ? parseFloat(priceEl.getAttribute('data-actualprice')) || 0 : 0;

  document.querySelectorAll(`${UPGRADE_INPUT}:checked`).forEach((input) => {
    if (input.disabled) return;
    const form = input.closest('addons-form');
    const priceNode = form && form.querySelector('[data-addons-currentPrice]');
    if (!priceNode) return;
    // data-price is dollars-as-float; parseFloat (not parseInt) so cents survive.
    cents += Math.round((parseFloat(priceNode.getAttribute('data-price')) || 0) * 100);
  });

  const money = formatUpgradeMoney(cents);
  totalEl.innerHTML = money;
  totalEl.setAttribute('data-mainProPrice', cents / 100);

  // The headline price tracks upgrades as well. data-actualprice is deliberately
  // left alone — it's the base this function sums from, so writing the total back
  // into it would compound on the next recalc.
  if (priceEl && priceEl.innerHTML !== money) priceEl.innerHTML = money;

  // ADA B3 (WCAG 4.1.3): announced via a separate status node, not a live region on the control itself.
  if (!announce) return;
  const status = document.querySelector('[data-upgrade-status]');
  if (status) status.textContent = `${window.variantStrings && window.variantStrings.addToCart || 'Add to cart'} ${money}`;
}

window.TigerTough = window.TigerTough || {};
window.TigerTough.recalcUpgradeTotal = recalcUpgradeTotal;

// Mirrors Liquid's `handleize` so the server-rendered match and this one agree.
function handleizeValue(value) {
  return String(value == null ? '' : value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Repoint the seat back pocket at the variant matching the newly selected seat
 * cover colour. Colours with no pocket equivalent (Realtree, the Vipers) and
 * sold-out matches fall back to the first available variant.
 */
function syncPocketVariant(variant) {
  const form = document.querySelector('addons-form[data-upgrade-group="pocket"]');
  if (!form || !variant) return;

  const json = form.querySelector('[data-pocket-variants]');
  const idInput = form.querySelector('.variant--id');
  if (!json || !idInput) return;

  let variants;
  try {
    variants = JSON.parse(json.textContent);
  } catch (e) {
    return;
  }
  if (!Array.isArray(variants) || !variants.length) return;

  const wanted = handleizeValue(variant.options && variant.options[parseInt(form.dataset.colorIndex, 10)]);
  const available = variants.filter((v) => v.available);
  const chosen = available.find((v) => handleizeValue(v.title) === wanted)
    || available.find((v) => handleizeValue(v.title) === 'black')
    || available[0]
    || variants[0];

  idInput.value = chosen.id;

  const priceNode = form.querySelector('[data-addons-currentPrice]');
  if (priceNode) {
    const suffix = priceNode.getAttribute('data-price-suffix');
    priceNode.setAttribute('data-price', chosen.price / 100);
    priceNode.textContent = formatUpgradeMoney(chosen.price) + (suffix ? ' ' + suffix : '');
  }

  // No recalcUpgradeTotal() here: variant:change fires before renderProductInfo
  // has written the new base to data-actualprice, and that call recalcs anyway.
}

document.addEventListener('variant:change', (evt) => {
  syncPocketVariant(evt.detail && evt.detail.variant);
});

class AddonsForm extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.input = this.querySelector(UPGRADE_INPUT);
    this.panel = this.querySelector('[data-customOptions]');
    this.card = this.querySelector('.pdp-upgrade__card');
    if (!this.input) return;

    this.input.addEventListener('change', this.onToggle.bind(this));
    this.render();
  }

  onToggle() {
    if (this.input.checked) {
      const group = this.dataset.upgradeGroup;
      document.querySelectorAll('addons-form').forEach((form) => {
        if (form === this || form.dataset.upgradeGroup !== group) return;
        if (typeof form.deselect === 'function') form.deselect();
      });
    }
    this.render();
    recalcUpgradeTotal(true);

    // Collapsing a taller sibling above this card can shove it off-screen, and a
    // card sitting low in the viewport opens its panel off the bottom; park it
    // under the header in those cases once the panels settle.
    if (this.input.checked && this.card) {
      // No transition to wait out under reduced motion.
      const settle = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 0
        : UPGRADE_PANEL_TRANSITION_MS;
      setTimeout(() => Utility.parkBelowHeaderIfNeeded(this.card, UPGRADE_SCROLL_GAP), settle);
    }
  }

  /** Clear this card without firing another round of change handlers. */
  deselect() {
    if (!this.input || !this.input.checked) return;
    this.input.checked = false;
    this.render();
  }

  render() {
    const open = !!(this.input && this.input.checked);
    if (this.card) this.card.classList.toggle('is-selected', open);
    if (this.input) this.input.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (!this.panel) return;

    this.panel.classList.toggle('is-open', open);

    // ADA A3 (WCAG 2.4.3): `inert`, not `hidden`, so the grid-rows transition still animates.
    this.panel.toggleAttribute('inert', !open);
  }
}

customElements.define('addons-form', AddonsForm);

class AddonsVariantSelects extends HTMLElement {
  constructor() {
    super();
    this.form = this.closest('addons-form');
    this.addOnSelection = this.form && this.form.querySelector(UPGRADE_INPUT);
    this.variant_json = this.form && this.form.querySelector('[data-addons-variantJSON]');
    this.variantPicker = this.dataset.type;
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  onVariantChange() {
    this.setCurrentVariant();

    if (!this.currentVariant) {
      this.toggleAddButton('disable');
      this.updateOptionLabels();
      recalcUpgradeTotal();
      return;
    }

    const priceNode = this.form.querySelector('[data-addons-currentPrice]');
    if (priceNode) priceNode.setAttribute('data-price', this.currentVariant.price / 100);

    this.renderProductInfo(this.currentVariant, this.closest('[data-product-grid]'));
    this.updateID(this.currentVariant);
    this.toggleAddButton('enable');
    recalcUpgradeTotal();
  }

  _getOptionsFromSelect() {
    return Array.from(this.querySelectorAll('.addons-variant_selector')).map((selector) => selector.value);
  }

  _getOptionsFromRadio() {
    return Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return checked ? checked.getAttribute('data-value') : null;
    });
  }

  setCurrentVariant() {
    this.currentVariant = false;
    const options = this.variantPicker == 'variant-select' ? this._getOptionsFromSelect() : this._getOptionsFromRadio();
    this._getVariantData().find((variant) => {
      const matches = variant.options.every((option, index) => options[index] == option);
      if (matches) {
        this.currentVariant = variant;
        return true;
      }
      return false;
    });
  }

  updateID(currentVariant) {
    if (!currentVariant) return;
    const input = this.form.querySelector('input.variant--id');
    if (input) input.value = currentVariant.id;
  }

  updateOptionLabels(currentVariant) {
    this.form.querySelectorAll('[data-addons-optionindex]').forEach((option) => {
      const label = option.querySelector('.selected-option');
      if (!label) return;
      if (currentVariant) {
        label.innerHTML = currentVariant['option' + option.getAttribute('data-addons-optionindex')];
      } else {
        const selected = option.querySelector('input[type="radio"]:checked');
        if (selected) label.innerHTML = selected.value;
      }
    });
  }

  renderProductInfo(currentVariant, container) {
    if (!currentVariant || !container) return;

    const priceElement = container.querySelector('[data-addons-currentPrice]');
    if (priceElement) {
      // Append the "+" suffix separately — formatMoney would strip it.
      const suffix = priceElement.getAttribute('data-price-suffix');
      priceElement.innerHTML = formatUpgradeMoney(currentVariant.price) + (suffix ? ` ${suffix}` : '');
    }

    this.updateOptionLabels(currentVariant);

    const featuredImage = container.querySelector('[data-addons-feauredImage]');
    if (featuredImage && currentVariant.featured_image && currentVariant.featured_image.src) {
      featuredImage.src = currentVariant.featured_image.src;
      featuredImage.srcset = currentVariant.featured_image.src;
    }
  }

  toggleAddButton(status) {
    if (!this.addOnSelection) return;
    const unavailable = status == 'disable' || (this.currentVariant && !this.currentVariant.available);
    this.addOnSelection.disabled = unavailable;
    // An unavailable variant must not stay in the cart total.
    if (unavailable && this.addOnSelection.checked) {
      this.addOnSelection.checked = false;
      if (this.form && typeof this.form.render === 'function') this.form.render();
    }
  }

  _getVariantData() {
    if (!this.variantData) {
      this.variantData = this.variant_json ? JSON.parse(this.variant_json.textContent) : [];
    }
    return this.variantData;
  }
}
customElements.define('addons-variant-selects', AddonsVariantSelects);

class AddonsVariantRadios extends AddonsVariantSelects {}
customElements.define('addons-variant-radios', AddonsVariantRadios);
